/**
 * Per-callout-thread action items section, embedded in the callout panel.
 * Compact list + create; the full board lives on the Action Items page.
 * Feature-gated on `action_items` — renders nothing without access.
 */

import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import Link from '@amzn/awsui-components-core/link';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import {
  selectFeatureAccessible,
  useGetPermissionsQuery,
} from 'src/features/api/permissionsApi';
import {
  useCreateThreadActionItemMutation,
  useGetThreadActionItemsQuery,
} from 'src/features/api/actionItemsApi';
import {
  ActionItemRecord,
  ActionItemThreadAnchor,
} from 'src/types/actionItems.types';
import { ACTION_ITEMS_FEATURE_ID } from 'src/constants/actionItems';
import { useActiveDashboard } from 'src/features/api/useActiveDashboard';
import { relativeTime } from 'src/components/table/relativeTime';
import { CalloutAuthor } from 'src/components/table/CalloutAuthor';
import { ActionItemForm } from './ActionItemForm';
import { ActionItemStatusIndicator } from './ActionItemStatus';
import { ActionItemDetail } from './ActionItemDetail';
import { isOverdue } from './etaUtils';

export interface ThreadActionItemsProps {
  anchor: ActionItemThreadAnchor;
}

export const ThreadActionItems: React.FC<ThreadActionItemsProps> = ({
  anchor,
}) => {
  const { data: permissions } = useGetPermissionsQuery();
  const accessible = selectFeatureAccessible(
    permissions,
    ACTION_ITEMS_FEATURE_ID,
  );
  // Items are keyed per (metricKey, metricCategory); a category-less anchor
  // (legacy All-tab selections) cannot address them, so skip the section.
  const anchorComplete = Boolean(anchor.metricCategory);
  const { data: items, isError } = useGetThreadActionItemsQuery(anchor, {
    skip: !accessible || !anchorComplete,
  });
  const [createForThread] = useCreateThreadActionItemMutation();
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useActiveDashboard();
  const onActionItemsPage = location.pathname.endsWith('/action-items');
  const [detailOf, setDetailOf] = useState<ActionItemRecord | null>(null);

  if (!accessible || !anchorComplete) return null;

  return (
    <div
      className="metric-callout-panel__section"
      data-testid="thread-action-items"
    >
      <div className="metric-callout-panel__section-header">
        <div className="metric-callout-panel__label">
          Action items{items?.length ? ` (${items.length})` : ''}
        </div>
        <Button
          variant="inline-link"
          iconName="add-plus"
          onClick={() => setCreating(true)}
          data-testid="thread-action-items-create"
        >
          Add
        </Button>
      </div>
      <SpaceBetween direction="vertical" size="s">
        {(items ?? []).map((item) => (
          // Whole row opens the detail; the row is the keyboard control.
          <div
            key={item.id}
            className={
              isOverdue(item)
                ? 'metric-callout-panel__comment metric-callout-panel__ai-row metric-callout-panel__ai-row--overdue'
                : 'metric-callout-panel__comment metric-callout-panel__ai-row'
            }
            data-testid="thread-action-item-row"
            role="button"
            tabIndex={0}
            onClick={() => setDetailOf(item)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault(); // Space would also scroll the page
                setDetailOf(item);
              }
            }}
          >
            <SpaceBetween direction="vertical" size="xxs">
              <span className="metric-callout-panel__ai-heading">
                {item.heading}
              </span>
              <div className="metric-callout-panel__ai-meta">
                <ActionItemStatusIndicator status={item.status} />
                <Box fontSize="body-s" color="text-body-secondary">
                  {item.assignee ? (
                    <>
                      <CalloutAuthor author={item.assignee} />
                      {' · '}
                    </>
                  ) : null}
                  {item.eta ? `due ${item.eta} · ` : ''}
                  {relativeTime(item.updatedAt)}
                </Box>
              </div>
            </SpaceBetween>
          </div>
        ))}
        {items && items.length === 0 && (
          <Box color="text-body-secondary">No action items.</Box>
        )}
        {isError && (
          <Box color="text-status-error" fontSize="body-s">
            Couldn’t load action items for this thread.
          </Box>
        )}
        {/* Hidden when already on the Action Items page. */}
        {!onActionItemsPage && (
          <Box fontSize="body-s">
            <Link
              fontSize="inherit"
              onFollow={() => navigate(`/${slug}/action-items`)}
              data-testid="thread-action-items-view-all"
            >
              View all in Action Items →
            </Link>
          </Box>
        )}
      </SpaceBetween>
      {detailOf && (
        <ActionItemDetail
          record={detailOf}
          onDismiss={() => setDetailOf(null)}
          showThreadLink={false}
        />
      )}
      {creating && (
        <ActionItemForm
          title="Add action item"
          existing={null}
          onSubmit={(draft) => createForThread({ anchor, draft }).unwrap()}
          onDismiss={() => setCreating(false)}
        />
      )}
    </div>
  );
};
